import React from "react";

import _ from "lodash";

import { FILTER_TAG } from "components/Filter/TypeFilter";
import { IOption } from "model/application/components";
import { ITeamSelector } from "model/entities/Team";

import {
  IHierarchyDependency,
  IMetaHierarchyDependency,
  IMetaLevelType,
} from "../../../../model/entities/HierarchyDependency";

export interface Node {
  label: React.ReactNode;
  value: string;
  children?: Array<Node>;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  showCheckbox?: boolean;
  title?: string;
}

/**
 * This function is responsible for converting the actual IHierarchyDependency type to the type
 * of the CheckboxTree component
 * @param level: number The level of the root, it will be always  zero
 * @param hierarchy: IHierarchyDependency[] A IHierarchyDependency
 * @param nodeFather: string | undefined Start with
 * undefined because the root do not have parent and will be actualized on each recursion
 */

export const convertHierarchyTypes = (
  hierarchy: IHierarchyDependency[],
  metaHierarchy: IMetaHierarchyDependency,
  level?: number,
  father?: string,
): Node[] => {
  if (!hierarchy || !Array.isArray(hierarchy)) {
    return [];
  }

  const hierarchyLevel = level ? level : 0;
  const mappedHierarchy: Node[] = [];

  const levelTagRelatedToTheLevel: string | undefined = Object.keys(
    metaHierarchy,
  ).find((tag) => {
    return (
      metaHierarchy[tag as keyof IMetaHierarchyDependency]
        ?.level_type_number === hierarchyLevel
    );
  });

  const levelTagRelatedToTheLevelPlus1: string | undefined = Object.keys(
    metaHierarchy,
  ).find((tag) => {
    return (
      metaHierarchy[tag as keyof IMetaHierarchyDependency]
        ?.level_type_number ===
      hierarchyLevel + 1
    );
  });

  const filteredHierarchy = hierarchy
    .filter((h) => {
      return (
        h.level_type_number ===
        metaHierarchy[
          (levelTagRelatedToTheLevel as keyof IMetaHierarchyDependency) ?? ""
        ]?.level_type_number
      );
    })
    .filter((h) => {
      return !father || h.level_father === father;
    });

  for (const h of filteredHierarchy) {
    if (levelTagRelatedToTheLevelPlus1) {
      // if the level has some kids
      const newHierarchy: Node = {
        value: h.level_name.toLowerCase().replace(/\s/g, ""),
        label: h.level_name,
        children: convertHierarchyTypes(
          hierarchy,
          metaHierarchy,
          hierarchyLevel + 1,
          h.level_name,
        ),
      };

      if (newHierarchy?.children?.length === 0) {
        delete newHierarchy.children;
      }

      mappedHierarchy.push(newHierarchy);
    } else {
      // if the level does not have any kids
      mappedHierarchy.push({
        value: h.level_name,
        label: h.level_name,
      });
    }
  }
  return mappedHierarchy;
};

/**
 * This function will return all the hierarchy that have to be expanded to show
 * the nodes that match a given keyword
 * @param nodes: any[] Is a node list. On this case any is the object
 * IHierarchyDependency converted to CheckboxTree format. You can see the format on the utils.test.ts file on this directory
 * @param keyword: string Is a string that will have the keyword searched
 */
const getNodeLabel = (label: React.ReactNode): string => {
  if (label == null) return "";
  if (typeof label === "string" || typeof label === "number") {
    return String(label).toLowerCase();
  }
  return "";
};

export const findExpandedHierarchy = (nodes: any[], keyword: string) => {
  const searchedNodes = [];
  const normalizedKeyword = (keyword ?? "").toLowerCase();
  /* eslint-disable sonarjs/null-dereference */
  for (const n of nodes) {
    const normalizedLabel = getNodeLabel(n.label) ?? "";
    if (n.children) {
      const nextSearchedNodes = findExpandedHierarchy(n.children, keyword);
      if (nextSearchedNodes.length > 0) {
        n.children = nextSearchedNodes;
      } else if (normalizedLabel.includes(normalizedKeyword)) {
        if (nextSearchedNodes.length > 0) {
          n.children = nextSearchedNodes;
        }
      }
      if (
        nextSearchedNodes.length > 0 ||
        normalizedLabel.includes(normalizedKeyword)
      ) {
        searchedNodes.push(n);
      }
    } else {
      if (normalizedLabel.includes(normalizedKeyword)) {
        searchedNodes.push(n);
      }
    }
  }
  /* eslint-enable sonarjs/null-dereference */
  return searchedNodes;
};
/**
 * This function get all labels of a converted IHierarchyDependency to the format demanded by the CheckboxTree
 * @param nodes: any[] Is a node list. On this case any is the object
 * IHierarchyDependency converted to CheckboxTree format. You can see the format on the utils.test.ts file on this directory
 * @param values: string[] : Start empty and in the end will have all the values
 */
export const getAllValues = (nodes: any[], values: string[]) => {
  for (const n of nodes) {
    if (n.children) {
      values.push(n.value);
      getAllValues(n.children, values);
    } else values.push(n.value);
  }
  return _.compact(values);
};

/**
 * This function is responsible to make the link between hierarchy and level filters when a hierarchy is selected
 * @param hierarchyFilterSelectedValues string[] Is the array of checked hierarchies on the hierarchy filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 */
export const getTeamsFromLabels = (
  hierarchyFilterSelectedValues: string[],
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
) => {
  return _(allTeamsObject)
    .filter((t) => {
      let result = false;
      Object.keys(metaHierarchy).forEach((key) => {
        const metaKey = key as keyof IMetaHierarchyDependency;
        if (metaHierarchy[metaKey]?.level_type_number === -1) {
          const teamValue = (t as any)[key];
          if (teamValue && hierarchyFilterSelectedValues.includes(teamValue)) {
            result = true;
          }
        }
      });
      return result;
    })
    .map((t) => t.id)
    .compact()
    .value();
};

/**
 * This function is responsible to make the link between hierarchy and level filters when a hierarchy is selected
 * @param hierarchyFilterSelectedValues string[] Is the array of checked hierarchies on the hierarchy filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 */
export const getTeamsFromLabel = (
  hierarchyFilterSelectedValues: string[],
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
) => {
  return _(allTeamsObject)
    .filter((t) => {
      let result = false;
      Object.keys(metaHierarchy).forEach((key) => {
        const teamValue = (t as any)[key];
        if (teamValue && hierarchyFilterSelectedValues?.includes(teamValue)) {
          result = true;
        }
      });
      return result;
    })
    .map((t) => t.id)
    .compact()
    .value();
};

/**
 * This function is responsible to make the link between hierarchy and level filters when a hierarchy is selected
 * @param hierarchyFilterSelectedValues string[] Is the array of checked hierarchies on the hierarchy filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 */
export const getTeamsFromHierarchy = (
  hierarchyFilterSelectedValues: string[],
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
) => {
  const cleanhierarchyFilterSelectedValues = _.compact(
    hierarchyFilterSelectedValues,
  );
  const teamsFiltered = _.filter(allTeamsObject, (t) => {
    let result = false;
    const metaHierarchyKeys = _.keys(metaHierarchy);
    for (const key of metaHierarchyKeys) {
      const metaKey = key as keyof IMetaHierarchyDependency;
      const teamValue = (t as any)[key];
      if (
        (metaHierarchy[metaKey]?.level_type_number ?? -1) >= 0 &&
        _.includes(cleanhierarchyFilterSelectedValues, teamValue)
      ) {
        result = true;
      }
    }
    return result;
  });

  const teamsIds = _.compact(_.map(teamsFiltered, "id"));
  return _.values(teamsIds);
};

/**
 * This function is responsible for linking hierarchy and teams filter when team filter is selected
 * @param teamFilterSelectedValues string[] Is the array os team id's that were selected on the filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 */
export const getLabelsFromTeams = (
  teamFilterSelectedValues: string[] | undefined,
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
) => {
  const hierarchyToCheck = {};
  const selectedTeams: ITeamSelector[] = allTeamsObject.filter(
    (team) =>
      !teamFilterSelectedValues || teamFilterSelectedValues.includes(team.id),
  );
  for (const t of selectedTeams) {
    Object.keys(metaHierarchy).forEach((tag) => {
      const metaKey = tag as keyof IMetaHierarchyDependency;
      const teamValue = (t as any)[tag];
      if (
        metaHierarchy[metaKey]?.level_type_number === -1 &&
        teamValue &&
        teamValue.length > 0
      ) {
        (hierarchyToCheck as Record<string, boolean>)[teamValue] = true;
      }
    });
  }
  return _.compact(_.keys(hierarchyToCheck));
};
export const getLabelsFromHierarchy = (
  teamFilterSelectedValues: string[] | undefined,
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
  metaLevelType: IMetaLevelType,
) => {
  const hierarchyToCheck = {};
  const selectedTeams: ITeamSelector[] = allTeamsObject.filter(
    (team) =>
      !teamFilterSelectedValues || teamFilterSelectedValues.includes(team.id),
  );
  for (const t of selectedTeams) {
    Object.keys(metaHierarchy).forEach((tag) => {
      const metaKey = tag as keyof IMetaHierarchyDependency;
      const teamValue = (t as any)[tag];
      if (
        metaHierarchy[metaKey]?.level_type_number === -1 &&
        metaHierarchy[metaKey]?.level_type_name ===
          metaLevelType.level_type_name &&
        teamValue &&
        teamValue.length > 0
      ) {
        (hierarchyToCheck as Record<string, boolean>)[teamValue] = true;
      }
    });
  }
  return _.compact(_.keys(hierarchyToCheck));
};

/**
 * This function is responsible for linking hierarchy and teams filter when team filter is selected
 * @param teamFilterSelectedValues string[] Is the array os team id's that were selected on the filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 */
export const getHierarchyFromTeams = (
  teamFilterSelectedValues: string[] | undefined,
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
) => {
  const hierarchyToCheck = {};
  const selectedTeams: ITeamSelector[] = allTeamsObject.filter(
    (team) =>
      !teamFilterSelectedValues || teamFilterSelectedValues.includes(team.id),
  );

  for (const t of selectedTeams) {
    _.keys(metaHierarchy).forEach((tag) => {
      const metaKey = tag as keyof IMetaHierarchyDependency;
      const teamValue = (t as any)[tag];
      if ((metaHierarchy[metaKey]?.level_type_number ?? -1) >= 0 && teamValue) {
        (hierarchyToCheck as Record<string, boolean>)[teamValue] = true;
      }
    });
  }

  return _.compact(_.keys(hierarchyToCheck));
};

/**
 * This function is responsible to make the link between hierarchy and level filters when a hierarchy is selected
 * @param hierarchyFilterSelectedValues string[] Is the array of checked hierarchies on the hierarchy filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 * @param labelPerTeamTag
 */
export const getTeamsFromLabelPerTeam = (
  hierarchyFilterSelectedValues: string[],
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
  labelPerTeamTag: string,
) => {
  return _(allTeamsObject)
    .filter((t) => {
      let result = false;
      Object.keys(metaHierarchy).forEach((key) => {
        const metaKey = key as keyof IMetaHierarchyDependency;
        const teamValue = (t as any)[key];
        if (
          labelPerTeamTag ===
            FILTER_TAG.LABELS.concat(
              "_",
              metaHierarchy[metaKey]?.level_type_name
                ?.toLowerCase()
                .replace(/ /g, "_") ?? "",
            ) &&
          metaHierarchy[metaKey]?.level_type_number === -1
        ) {
          if (
            hierarchyFilterSelectedValues &&
            teamValue &&
            hierarchyFilterSelectedValues.includes(teamValue)
          ) {
            result = true;
          }
        }
      });
      return result;
    })
    .map((t) => t.id)
    .compact()
    .value();
};
/**
 * This function is responsible for linking hierarchy and teams filter when team filter is selected
 * @param teamFilterSelectedValues string[] Is the array os team id's that were selected on the filter
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 * @param metaHierarchy
 * @param labelPerTeamTag
 */
export const getLabelPerTeamFromTeams = (
  teamFilterSelectedValues: string[] | undefined,
  allTeamsObject: ITeamSelector[],
  metaHierarchy: IMetaHierarchyDependency,
  labelPerTeamTag: string,
) => {
  const hierarchyToCheck = {};
  const selectedTeams: ITeamSelector[] = allTeamsObject.filter(
    (team) =>
      !teamFilterSelectedValues || teamFilterSelectedValues.includes(team.id),
  );
  for (const t of selectedTeams) {
    Object.keys(metaHierarchy).forEach((tag) => {
      const metaKey = tag as keyof IMetaHierarchyDependency;
      const teamValue = (t as any)[tag];
      if (
        labelPerTeamTag ===
          FILTER_TAG.LABELS.concat(
            "_",
            metaHierarchy[metaKey]?.level_type_name
              ?.toLowerCase()
              .replace(/ /g, "_") ?? "",
          ) &&
        metaHierarchy[metaKey]?.level_type_number === -1 &&
        teamValue &&
        teamValue.length > 0
      ) {
        (hierarchyToCheck as Record<string, boolean>)[teamValue] = true;
      }
    });
  }
  return _.compact(_.keys(hierarchyToCheck));
};

export const checkIfHierarchyIsOk = (node: Node[]) => {
  // eslint-disable-next-line no-warning-comments -- legacy comment, to review
  // FIXME: make this function recursive. Not the time to do it right now
  const level1: Record<string, boolean> = {};
  const level2: Record<string, boolean> = {};
  const level3: Record<string, boolean> = {};
  const level4: Record<string, boolean> = {};
  let result = true;
  for (const n of node) {
    if (level1[n.value]) result = false;
    level1[n.value] = true;
    if (!n.children) {
      continue;
    }
    for (const n2 of n.children) {
      if (level2[n2.value]) result = false;
      level2[n2.value] = true;
      if (!n2.children) {
        continue;
      }
      for (const n3 of n2.children) {
        if (level3[n3.value]) result = false;
        level3[n3.value] = true;
        if (!n3.children) {
          continue;
        }
        for (const n4 of n3.children) {
          if (level4[n4.value]) result = false;
          level4[n4.value] = true;
        }
      }
    }
  }
  const all: Record<string, boolean> = {};
  for (const i of [
    ...Object.keys(level1),
    ...Object.keys(level2),
    ...Object.keys(level3),
    ...Object.keys(level4),
  ] as string[]) {
    if (all[i]) result = false;
    all[i] = true;
  }

  return result;
};

/**
 * This function is responsible for linking hierarchy, labels and teams filter when owners filter is selected
 * @param teamsIds string[] Is the array of teams' ids that were deducted from the filters
 * @param allTeamsObject ITeamSelector[] Is the object that have all teams of the selected client
 */
export const getMobileUsersFromTeamsIds = (
  teamsIds: string[],
  allTeamsObject: ITeamSelector[],
) => {
  return allTeamsObject
    .filter((t) => teamsIds && teamsIds.includes(t.id))
    .reduce((acc, curr) => {
      return acc.concat(curr.mobile_users.map((u) => u.key));
    }, [] as string[]);
};

// Names get pasted from exports or from another screen, where a stray double space or an accent
// is common. Match on a normalized form so a filter option is found whatever the exact typing.
const normalizeForSearch = (value: string) =>
  _.deburr(value).toLowerCase().replace(/\s+/g, " ").trim();

export const filterOptionsBySearch = (
  options: IOption[] | undefined,
  search: string,
): IOption[] => {
  const normalizedSearch = normalizeForSearch(search ?? "");
  if (!normalizedSearch) {
    return options ?? [];
  }
  return _.filter(options ?? [], (option) =>
    normalizeForSearch(option.label ?? "").includes(normalizedSearch),
  );
};

export function formatOptions(
  options?: (Object | undefined)[],
): IOption<string>[] {
  return _.compact(
    _.map(options, (opt) => {
      if (!opt) return;
      const optRecord = opt as Record<string, any>;
      const key = optRecord["id"] || optRecord["key"] || optRecord["_id"];
      const label =
        optRecord["name"] ||
        optRecord["label"] ||
        optRecord["_name"] ||
        optRecord["_displayed_name"] ||
        "";
      const checked = optRecord["checked"];
      return {
        key,
        label,
        value: optRecord,
        ...(checked === true || checked === false ? { checked } : {}),
      };
    }),
  );
}
